import type { SiteAnalytics as Data } from '../../../api/siteAnalyticsApi';
import { InfoTip } from '@shared-ui/components';

/**
 * The funnel, as nested steps.
 *
 * Every step is a subset of the one above it, so the percentages are of the
 * arrivals rather than of the previous step — "3% of everyone who arrived"
 * answers the question people actually ask, where "50% of the previous step"
 * flatters a step whose predecessor was already tiny.
 */
/**
 * What visits did — five measures over the same visits, NOT a funnel.
 *
 * It was drawn as one, with a caption saying each step was a subset of the one
 * above. It is not true and the chart showed it: `Carried an account` was 94 of
 * 97 where `Opened the lab` was 7, so the shape narrowed to a point and flared
 * back out. Only `Arrived` is a parent; the rest are independent things a visit
 * may or may not have done, and a bar of each against arrivals says that
 * plainly.
 *
 * `Followed a published link` is gone rather than shown as zero. It always was
 * zero and always would be: this page reads sittings on the science site, and
 * a link arrival is recorded at the redirector, which is no site at all. The
 * Acquisition tab is where a followed link is counted.
 */
export function VisitOutcomes({ data }: { data: Data }) {
  const { funnel } = data;
  const arrived = Math.max(1, funnel.visited);
  const outcomes = [
    { name: 'Opened a simulation', value: funnel.openedSimulation, fill: '#8b5cf6' },
    { name: 'Used the chemistry lab', value: funnel.usedLab, fill: '#a855f7' },
    { name: 'Carried an account (guests too)', value: funnel.signedIn, fill: '#db2777' },
  ];

  if (funnel.visited === 0) {
    return <section className="sa-panel"><p className="sa-empty">No sessions in this window.</p></section>;
  }

  return (
    <section className="sa-panel">
      <h2>
        What sessions did
        <InfoTip>
          <span className="sa-tip-p">
            Counted in sessions, not events. Each row is a share of the {funnel.visited.toLocaleString()} sessions
            that arrived — they are <strong>independent</strong>, not nested: a session can open the lab without
            opening a simulation, and nearly every session carries an account because the site mints a guest on
            the first page view. A followed link is counted on the Acquisition tab; it arrives at the
            redirector rather than at the site, so it is not one of these.
          </span>
        </InfoTip>
      </h2>
      {data.unmeasuredVisits > 0 && (
        <p className="sa-note">
          ⚠ {data.unmeasuredVisits.toLocaleString()} further session{data.unmeasuredVisits === 1 ? '' : 's'} in
          this window {data.unmeasuredVisits === 1 ? 'is' : 'are'} in <em>no</em> figure on this page — they
          were recorded before the site kept a per-session tally, so nothing can say what they did. The number
          falls to zero on its own as they age out.
        </p>
      )}
      <table className="sa-table">
        <thead>
          <tr><th>Outcome</th><th className="sa-num">Sessions</th><th className="sa-num">Of arrivals</th></tr>
        </thead>
        <tbody>
          <tr>
            <td className="sa-label"><strong>Arrived</strong></td>
            <td className="sa-num"><strong>{funnel.visited.toLocaleString()}</strong></td>
            <td className="sa-num">100.0%</td>
          </tr>
          {outcomes.map(o => (
            <tr key={o.name}>
              <td className="sa-label">
                <span className="sa-swatch" style={{ background: o.fill }} />
                {o.name}
              </td>
              <td className="sa-num">{o.value.toLocaleString()}</td>
              <td className="sa-num">{((o.value / arrived) * 100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sa-bars">
        {outcomes.map(o => (
          <div className="sa-bar-row" key={o.name}>
            <span className="sa-bar-label">{o.name}</span>
            <span className="sa-bar-track">
              <span className="sa-bar-fill" style={{ width: `${(o.value / arrived) * 100}%`, background: o.fill }} />
            </span>
            <span className="sa-bar-value">{((o.value / arrived) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </section>
  );
}
